import React, { useState, useCallback } from 'react';
import { generateLogoIdeas } from '../services/geminiService';
import { AJLogoLoader } from './AJLogoLoader';
import type { LogoConcept } from '../types';

type Stage = 'form' | 'generating' | 'results';

export const LogoDesigner: React.FC = () => {
  const [stage, setStage] = useState<Stage>('form');
  const [formData, setFormData] = useState({ name: '', description: '', style: 'Minimalist', color: '' });
  const [logoConcepts, setLogoConcepts] = useState<LogoConcept[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const onIdeasReady = useCallback((ideas: LogoConcept[]) => {
    setLogoConcepts(ideas.map(idea => ({ ...idea, imageUrl: undefined })));
  }, []);

  const onImageReady = useCallback((index: number, imageUrl: string) => {
    setLogoConcepts(prev => prev.map((concept, i) => i === index ? { ...concept, imageUrl } : concept));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setStage('generating');
    setError(null);
    setLogoConcepts([]);
    setSelectedIndex(null);
    try {
      await generateLogoIdeas(formData, onIdeasReady, onImageReady);
      setStage('results');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate logo ideas.');
      setStage('form');
    }
  }, [formData, onIdeasReady, onImageReady]);
  
  const conceptsToDisplay = stage === 'generating' && logoConcepts.length > 0 ? logoConcepts : (stage === 'results' ? logoConcepts : []);
  const isGenerating = stage === 'generating';

  const downloadLogo = (imageUrl: string, name: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${name}-logo.png`;
    link.click();
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-brand-cream via-white to-brand-cream/50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Left Side - Input Form */}
      <div className="w-full md:w-[450px] flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
        <div className="p-6">
          <div className="mb-8">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              ✨ AI Logo Designer
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Create unique logos with AI-powered design
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Company Name
              </label>
              <input 
                name="name" 
                value={formData.name} 
                onChange={handleInputChange} 
                placeholder="Enter your company name" 
                className="w-full bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 border border-gray-300 dark:border-gray-600 rounded-xl p-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all" 
                required 
                disabled={isGenerating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea 
                name="description" 
                value={formData.description} 
                onChange={handleInputChange} 
                placeholder="What does your company do?" 
                className="w-full bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 border border-gray-300 dark:border-gray-600 rounded-xl p-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all" 
                rows={4} 
                required 
                disabled={isGenerating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Style
              </label>
              <select 
                name="style" 
                value={formData.style} 
                onChange={handleInputChange} 
                className="w-full bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-xl p-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                disabled={isGenerating}
              >
                <option>Minimalist</option>
                <option>Geometric</option>
                <option>Abstract</option>
                <option>Vintage</option>
                <option>Modern</option>
                <option>Playful</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Color
              </label>
              <input 
                name="color" 
                value={formData.color} 
                onChange={handleInputChange} 
                placeholder="e.g., blue, #FF5733" 
                className="w-full bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 border border-gray-300 dark:border-gray-600 rounded-xl p-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all" 
                disabled={isGenerating}
              />
            </div>

            <button 
              type="submit" 
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:shadow-none"
            >
              {isGenerating ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating with AI...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  Generate Logos
                </>
              )}
            </button>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </form>

          {/* AI Model Info */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Powered by AI</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              ✨ Gemini 2.5 Pro + Imagen 3 with 🧠 Claude Sonnet 4 fallback
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Preview */}
      <div className="flex-1 overflow-y-auto p-8">
        {conceptsToDisplay.length === 0 && !isGenerating ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="mb-6 inline-block p-6 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-3xl">
                <svg className="w-20 h-20 text-purple-600 dark:text-purple-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-3">
                Create Your Perfect Logo
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Fill in the form on the left and let AI generate unique logo concepts for your brand
              </p>
            </div>
          </div>
        ) : (
          <div>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                {isGenerating ? '🎨 Generating Your Logos...' : '✨ Your Logo Concepts'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {isGenerating ? 'AI is creating unique designs for you' : `${conceptsToDisplay.length} concepts ready • Click to view full size`}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {conceptsToDisplay.map((concept, index) => (
                <div 
                  key={index} 
                  className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg border-2 transition-all duration-200 overflow-hidden ${
                    selectedIndex === index 
                      ? 'border-purple-500 ring-4 ring-purple-200 dark:ring-purple-900/50' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                  }`}
                  onClick={() => setSelectedIndex(index)}
                >
                  <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center p-8">
                    {concept.imageUrl ? (
                      <img 
                        src={concept.imageUrl} 
                        alt={concept.description} 
                        className="object-contain w-full h-full cursor-pointer hover:scale-105 transition-transform" 
                      />
                    ) : (
                      <AJLogoLoader size="lg" text="Generating logo..." />
                    )}
                  </div>
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{concept.description}</p>
                    {concept.imageUrl && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          downloadLogo(concept.imageUrl!, formData.name);
                        }}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium py-2 rounded-lg transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Logo
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};